import React, { useEffect, useMemo, useState } from 'react'

import { Box, Button, Flex, HStack, Heading, Spacer } from '@chakra-ui/react'

import ApplicationApprovalModal from '../components/ApplicationApprovalModal'

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable from '@/components/Table/FullTable/FullTable'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'

import { useApplicationType } from '@/features/applicationTypes/useApplicationType'
import { useApplication } from '../useApplication'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'

const columnHelper = createColumnHelper()

const ApplicationApprovals = () => {
  // Local State
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:applicationApproval`, {})
  const query = convertTableStateToQuery(storedTableState)

  const [applicationCategoryOptions, setApplicationCategoryOptions] = useState([])
  const [applicationTypeOptions, setApplicationTypeOptions] = useState([])

  const [modalDetail, setModalDetail] = useState({
    show: false,
    type: '',
    id: '',
  })

  const { useFetchApplicationApprovals } = useApplication()
  const { useFetchAllApplicationTypes } = useApplicationType()
  const { data: applicationResponse } = useFetchApplicationApprovals(query)
  const { data: applicationTypeResponse } = useFetchAllApplicationTypes({ page: 1, pageSize: 0 })

  useEffect(() => {
    if (applicationTypeResponse?.data) {
      const allCategories = [...new Set(applicationTypeResponse.data.map((type) => type.category))]
      setApplicationCategoryOptions(
        allCategories.map((category) => ({
          value: category,
          label: category,
        }))
      )

      setApplicationTypeOptions(
        applicationTypeResponse.data.map((type) => ({
          value: type.name,
          label: type.name,
        }))
      )
    }
  }, [applicationTypeResponse?.data])

  const handleOpenApprovalModal = (e, type, id) => {
    e.stopPropagation()
    setModalDetail({
      show: true,
      type: type,
      id: id,
    })
  }

  const defaultColumns = useMemo(
    () => [
      columnHelper.display({
        id: 'action',
        size: 80,
        enableHiding: false,
        enableColumnFilter: false,
        cell: ({ getValue, row: { original } }) => {
          const value = getValue()
          return (
            <HStack>
              {original?.status === 'required' && (
                <>
                  <Button colorScheme="green" onClick={(e) => handleOpenApprovalModal(e, 'approve', value)}>
                    Approve
                  </Button>
                  <Button colorScheme="red" onClick={(e) => handleOpenApprovalModal(e, 'reject', value)}>
                    Reject
                  </Button>
                </>
              )}
            </HStack>
          )
        },
      }),
      columnHelper.accessor('categoryName', {
        header: () => <span>Category</span>,
        meta: {
          filterVariant: FilterType.SELECT,
          filterOptions: applicationCategoryOptions,
        },
      }),
      columnHelper.accessor('type', {
        header: () => <span>Application Type</span>,
        meta: {
          filterVariant: FilterType.SELECT,
          filterOptions: applicationTypeOptions,
        },
      }),
      columnHelper.accessor('validity'),
      columnHelper.accessor('reference', {
        header: () => <span>Reference Should be Probided by Applicant</span>,
      }),
      columnHelper.accessor('applicantName', {
        header: () => <span>Applicant</span>,
      }),
      columnHelper.accessor('nameOfHolder'),
      columnHelper.accessor('status', {
        meta: {
          filterVariant: 'select ',
          filterOptions: [
            {
              value: 'required',
              label: 'required',
            },
            {
              value: 'upcoming',
              label: 'Upcoming',
            },
            {
              value: 'approved',
              label: 'approved',
            },
            {
              value: 'rejected',
              label: 'rejected',
            },
          ],
        },
      }),
    ],
    []
  )

  return (
    <>
      <ApplicationApprovalModal modalDetail={modalDetail} setModalDetail={setModalDetail} />
      <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
        <Flex p={5} borderBottom="1px solid #d8dbe0">
          <Heading> Application Approvals </Heading>
          <Spacer />
        </Flex>
        <Box p={5} overflowX="scroll">
          <FullTable
            data={applicationResponse?.data ? applicationResponse.data : []}
            rowCount={applicationResponse?.count}
            columnDef={defaultColumns}
            state={storedTableState}
            setState={setStoredTableState}
          />
        </Box>
      </Box>
    </>
  )
}

export default ApplicationApprovals
